"use client";

import { IBaseLabelValue } from "@/interfaces";
import { usePathname, useRouter } from "next/navigation";
import { cn, formatNumberWithDots, generateBillPaymentCallbackUrl } from "@/lib";
import { routes } from "@/routes";
import { useTranslations } from "next-intl";
import React, { useRef } from "react";
import PdfTemplate from "@/components/pdfTemplates/pdfTemplate";
import { IRawPurchasedWaterInquiryResData } from "@/interfaces/inquiries/governmental";
import { CustomButton, FlexBetween, FlexCenter, FlexCol } from "@/components";
import InputDataContent from "@/components/inquiryDetails/inputDataContent";
import DownloadPdf from "@/components/pdfTemplates/downloadPdf";
import { IPayFinesPayload } from "@/interfaces/inquiries/vehicle";
import { usePayFinesMutation } from "@/hooks";
import { useSession } from "next-auth/react";
import { useAppStore } from "@/store";

const PhoneBillImageInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedWaterInquiryResData;
}) => {
  const { data: session } = useSession();
  const {
    isPending: isPaySubmitting,
    mutate: PayFineFn,
    error: paySubmittingError,
    reset: resetPay,
  } = usePayFinesMutation(session?.user.token);
  const t = useTranslations("serviceResponseDetails");
  const router = useRouter();
  const response = data.data.data;
  if (!response) {
    return (
      <div className="shadow-main relative w-full rounded-2xl bg-white p-8">
        <div
          className="mx-auto max-w-[500px] rounded-lg p-4 text-center text-white"
          style={{ backgroundColor: "#5a4bd2" }}
        >
          برای این شناسه قبضی صادر نشده
        </div>
      </div>
    );
  }
  const { bill_id } = data.data.input;
  const amount = response?.amount
    ? `${formatNumberWithDots(response.amount / 10)} تومان `
    : "-";
  const inputContent: IBaseLabelValue[] = [
    {
      label: t("electricityBill.inputLabel"),
      value: bill_id || "",
    },
  ];
  const resultContent: IBaseLabelValue[] = [
    {
      label: t("electricityBill.amount"),
      value: amount,
    },
    {
      label: t("electricityBill.payID"),
      value: response.payID || "-",
    },
    {
      label: t("electricityBill.date"),
      value: response.date || "-",
    },
  ];
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const pathName = usePathname();
  const { setPreviousPath } = useAppStore();
  const handlePayBillSubmission = () => {
    const payload: IPayFinesPayload = {
      amount: response.amount,
      callback_url: generateBillPaymentCallbackUrl(pathName, (data.data as any).purchase_id),
      bill_id: Number(response.billID),
      pay_id: Number(response.payID),
    };
    PayFineFn(
      { payload },
      {
        onSuccess: (data) => {
          if (data.data) {
            setPreviousPath(pathName);
            window.location.href = data.data?.payment_link;
          }
        },
      },
    );
  };
  return (
    <div className="shadow-main relative basis-[500px] rounded-2xl bg-linear-to-b from-rose-500 to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-8 text-center font-bold lg:text-lg">
          استعلام قبض آب
        </h2>

        <InputDataContent content={inputContent} />
        <FlexBetween className="mb-3 items-center">
          <h2 className="text-center text-lg font-bold">
            {t("postalCodeInquiry.resultTitle")}
          </h2>
          <DownloadPdf
            id="water-inquiry-download-pdf"
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
            fileName={`قبض آب - ${bill_id}`}
          />
        </FlexBetween>
        <ul
          className={cn(
            "shadow-main mb-10 flex flex-col gap-6 rounded-xl p-4 py-8 sm:p-8",
          )}
        >
          {resultContent.map((item, index) => (
            <li
              key={index}
              className={cn(
                "flex-between items-start gap-6 text-xs sm:text-sm md:gap-8 lg:text-base",
                item.value === "-" ? "hidden" : "flex",
              )}
            >
              <span className="min-w-max">{item.label}</span>
              <span className="text-start" style={{ color: "#5a2bd2" }}>
                {item.value}
              </span>
            </li>
          ))}
          {response?.amount != 0 ? (
            <CustomButton
              id="details-water-inquiry-back"
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
              onClick={handlePayBillSubmission}
              isLoading={isPaySubmitting}
            >
              پرداخت {amount}
            </CustomButton>
          ) : (
            <p className="bg-success-main mx-auto w-full max-w-[200px] rounded-lg px-3 py-2 text-center text-white">
              تسویه شده
            </p>
          )}
        </ul>

        <FlexCenter className="mt-4 flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            id="water-inquiry-download-pdf"
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
            fileName={`قبض آب - ${bill_id}`}
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-water-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-water-inquiry-new-request"
              onClick={() =>
                router.push(routes.services.Water_Bill_inquiry.rewritePath)
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
      {/* This div will be used for PDF generation - DetailCreditCard style */}
      <div
        ref={pdfContentRef}
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          width: "800px",
          fontFamily: "IRANYekan, sans-serif",
          margin: "2rem auto",
        }}
      >
        <PdfTemplate
          tableTitle="اطلاعات قبض آب"
          resultContent={resultContent}
          code={data.code}
          dateTime={data.created_at}
        />
      </div>
    </div>
  );
};

export default PhoneBillImageInquiryDetails;
